/**
 * CUS-2 — per-ticket custody handoff history.
 *
 * Unlike `useTicketCustodyEvents` (timing-only: event_kind + created_at),
 * this reads the FULL custody-event row — actor, role, reason, owner
 * transition, metadata — so the accountability trail can be surfaced.
 * Shares the `['ticket-custody-events', ...]` realtime invalidation via a
 * distinct query key so it refreshes with the rest of the custody UI.
 */
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import {
  toCustodyHistoryEntries,
  type CustodyHistoryEntry,
  type CustodyHistoryRow,
} from '@/lib/tickets/lifecycle/custodyHistory';

export function useTicketCustodyHistory(
  ticketId: string | null | undefined,
  enabled: boolean,
) {
  return useQuery({
    queryKey: ['ticket-custody-history', ticketId],
    enabled: enabled && !!ticketId,
    retry: false,
    queryFn: async (): Promise<CustodyHistoryEntry[]> => {
      const { data, error } = await (supabase as any)
        .from('ticket_custody_events')
        .select(
          'id, event_kind, from_owner, to_owner, actor_id, actor_role, reason, metadata, created_at',
        )
        .eq('ticket_id', ticketId)
        .order('created_at', { ascending: true });
      if (error) {
        console.error('[ticket-custody-history] read failed:', error);
        throw error;
      }
      return toCustodyHistoryEntries((data ?? []) as CustodyHistoryRow[]);
    },
  });
}
